/**
 * @format
 */

import React, { useRef, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from 'chart.js';
import { useWeeklyPremium } from '../hooks/useWeeklyPremium';
import { DashboardQueryParams } from '../types/dashboard.types';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

const options = {
  type: 'line',
  responsive: true,
  interaction: {
    mode: 'index' as const,
    intersect: false,
  },
  plugins: {
    legend: {
      position: 'top' as const,
      display: false,
    },
    title: {
      display: false,
      text: 'Weekly Premium Comparison',
    },
  },
  elements: {
    line: {
      tension: 0.4,
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
      border: {
        display: false,
      },
    },
    y: {
      grid: {
        display: true,
      },
      border: {
        display: false,
      },
    },
  },
};

interface WeeklyPremiumChartProps {
  params?: DashboardQueryParams;
}

const WeeklyPremiumChart: React.FC<WeeklyPremiumChartProps> = ({ params }) => {
  const chartRef = useRef<any>(null);
  const { chartData, loading, error } = useWeeklyPremium(params);

  useEffect(() => {
    if (chartRef.current && chartData.labels.length > 0) {
      const ctx = chartRef.current.ctx;
      const gradient = ctx.createRadialGradient(
        ctx.canvas.width / 2,
        ctx.canvas.height / 2,
        0,
        ctx.canvas.width / 2,
        ctx.canvas.height / 2,
        Math.max(ctx.canvas.width, ctx.canvas.height) / 2
      );
      gradient.addColorStop(0, 'rgba(0, 0, 0, 0.08)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

      chartRef.current.data.datasets[0].backgroundColor = gradient;
    }
  }, [chartData]);

  if (error) {
    return (
      <div style={{ color: 'red', padding: '20px' }}>
        Error loading weekly premium data: {error.message}
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        Loading weekly premium data...
      </div>
    );
  }

  const lineChartData = {
    labels: chartData.labels,
    datasets: [
      {
        label: 'This Week',
        data: chartData.currentWeekData,
        borderColor: 'rgb(151, 166, 252)',
        backgroundColor: 'rgba(0, 0, 0, 0)',
        fill: true,
      },
      {
        label: 'Previous Week',
        data: chartData.previousWeekData,
        borderColor: '#A1E3CB',
        borderDash: [5, 5],
        backgroundColor: 'rgba(255, 99, 132, 0.1)',
        fill: false,
      },
    ],
  };

  return (
    <div className="premium-chart-container">
      <div className="premiumm-chart-header">
        <span className="heading">Premium</span>
        <span style={{ color: 'rgba(28, 28, 28, 0.20)' }}>|</span>
        <div className="legend-wrapper">
          <div className="current-week"></div>
          <div>
            <span>Current Week </span>
            <span style={{ fontSize: '12px', fontWeight: '600' }}>
              ${chartData.currentWeekTotal.toLocaleString()}
            </span>
          </div>
        </div>
        <div className="legend-wrapper">
          <div className="previous-week"></div>
          <div>
            <span>Previous Week</span>
            <span style={{ fontSize: '12px', fontWeight: '600' }}>
              ${chartData.previousWeekTotal.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
      <Line
        ref={chartRef}
        width={892}
        height={273}
        data={lineChartData}
        options={options}
      />
    </div>
  );
};

export default WeeklyPremiumChart;
